import { useEffect, useState } from 'react';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: 'ri-brain-line',
    title: 'Skilled & Trained Professionals',
    description: 'Our team consists of highly qualified experts with proven track records in their respective fields.'
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Cost-Effective Business Solutions',
    description: 'Get premium quality services at competitive rates that fit your budget and maximize ROI.'
  },
  {
    icon: 'ri-puzzle-line',
    title: 'Flexible Service Packages',
    description: 'Customizable plans that adapt to your unique business needs and scale with your growth.'
  },
  {
    icon: 'ri-time-line',
    title: 'Fast Turnaround Time',
    description: 'Quick onboarding and rapid execution to get your projects completed on schedule.'
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Dedicated Account Management',
    description: 'A personal account manager ensures seamless communication and project success.'
  },
  {
    icon: 'ri-team-line',
    title: 'Extension of Your Team',
    description: 'We integrate seamlessly with your operations, working as a natural extension of your business.'
  }
];

const stats = [
  { number: 500, label: 'Projects Completed', suffix: '+' },
  { number: 98, label: 'Client Satisfaction', suffix: '%' },
  { number: 24, label: 'Support Available', suffix: '/7' }
];

export default function WhyChooseUs() {
  const [counters, setCounters] = useState(stats.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          stats.forEach((stat, index) => {
            let current = 0;
            const increment = stat.number / 50;
            const timer = setInterval(() => {
              current += increment;
              if (current >= stat.number) {
                setCounters(prev => {
                  const newCounters = [...prev];
                  newCounters[index] = stat.number;
                  return newCounters;
                });
                clearInterval(timer);
              } else {
                setCounters(prev => {
                  const newCounters = [...prev];
                  newCounters[index] = Math.floor(current);
                  return newCounters;
                });
              }
            }, 30);
          });
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('why-choose-us');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section id="why-choose-us" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Panel */}
          <div className="bg-gradient-to-br from-[#004aad] to-[#003080] rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-24 h-24 border-4 border-white rotate-45"></div>
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-12">
                Why Choose TechHeroesHub?
              </h2>
              <div className="space-y-8">
                {stats.map((stat, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="text-5xl font-bold text-[#ffbd59]">
                      {counters[index]}{stat.suffix}
                    </div>
                    <div className="text-lg text-gray-300">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-12 p-6 bg-white/10 rounded-2xl backdrop-blur-sm">
                <p className="text-lg leading-relaxed">
                  We operate as an extension of your team, not just another service provider.
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel - Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ffbd59] to-[#ffa726] flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}